import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../lib/apiClient";
import { useCurrentAppUser } from "../lib/useCurrentAppUser";
import type { UserProfile, ConnectionRelation } from "@shared/types";

interface ProfileResponse {
  profile: UserProfile;
  relation: ConnectionRelation;
  limited: boolean;
}

export function ProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: me } = useCurrentAppUser();
  const [data, setData] = useState<ProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Guards against a slower-to-resolve load() for a profile the user has
  // since navigated away from overwriting the screen with the wrong
  // person's (possibly connection-gated) profile data.
  const activeUserIdRef = useRef(userId);

  function load() {
    if (!userId) return;
    activeUserIdRef.current = userId;
    const requestUserId = userId;
    setLoading(true);
    api
      .get<ProfileResponse>(`/api/people/${userId}`)
      .then((result) => {
        if (activeUserIdRef.current === requestUserId) setData(result);
      })
      .catch((err) => {
        if (activeUserIdRef.current === requestUserId) {
          setError(err instanceof ApiError ? err.message : "Failed to load this profile.");
        }
      })
      .finally(() => {
        if (activeUserIdRef.current === requestUserId) setLoading(false);
      });
  }

  useEffect(load, [userId]);

  async function connect() {
    if (!userId) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/connections", { recipientId: userId });
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to send connection request.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p className="muted">Loading…</p>;
  if (error || !data) return <p className="error-text">{error ?? "Profile not found."}</p>;

  const { profile, relation, limited } = data;
  const isSelf = me?.id === profile.userId;

  return (
    <div className="content-col">
      <button className="link-btn" onClick={() => navigate("/network")}>
        ← Back to Network
      </button>

      <div className="card profile-card">
        <div className="profile-head">
          <div className="avatar profile-avatar">
            <svg width="22" height="22" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="5.5" r="2.75" stroke="currentColor" strokeWidth="1.3" />
              <path d="M2.75 13.25c0-2.485 2.351-4.5 5.25-4.5s5.25 2.015 5.25 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
          </div>
          <div>
            <h1 style={{ margin: 0 }}>{profile.fullName ?? profile.email}</h1>
            <p className="muted" style={{ margin: 0 }}>
              {profile.role.replace(/_/g, " ")}
              {profile.title ? ` · ${profile.title}` : ""}
            </p>
          </div>
        </div>

        {isSelf && <p className="muted">This is your profile — edit it from Settings.</p>}
        {!isSelf && relation === "none" && (
          <button className="btn btn-primary" disabled={busy} onClick={connect}>
            {busy ? "Sending…" : "Connect"}
          </button>
        )}
        {!isSelf && relation === "pending_sent" && <span className="status-chip status-draft">Request pending</span>}
        {!isSelf && relation === "pending_received" && (
          <p className="muted">This person wants to connect — respond from the Network page's Requests tab.</p>
        )}
        {!isSelf && relation === "connected" && (
          <div className="doc-actions">
            <span className="status-chip status-approved">Connected</span>
            <button className="btn btn-primary" onClick={() => navigate(`/messages/${userId}`)}>
              Message
            </button>
          </div>
        )}

        {limited ? (
          <p className="muted profile-limited-notice">🔒 Connect with {profile.fullName ?? profile.email} to see their bio and certifications.</p>
        ) : (
          <>
            {profile.bio && (
              <div className="profile-section">
                <h2 className="settings-section-title">Bio</h2>
                <p>{profile.bio}</p>
              </div>
            )}
            {profile.certifications.length > 0 && (
              <div className="profile-section">
                <h2 className="settings-section-title">Certifications</h2>
                <div className="profile-cert-list">
                  {profile.certifications.map((c) => (
                    <span className="status-chip status-archived" key={c}>
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
